import { Box, Chip, List, ListItem, Typography } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { IsTenantCoordinatorOrAdmin } from 'shared/permissions/IsTenantCoordinator';
import { IContractProductLine } from 'features/inventory/contracts/types';
import { useTranslation } from 'react-i18next';

interface ProductPricesCardProps {
  contractLines: IContractProductLine[];
}

function ProductPricesCard(props: ProductPricesCardProps) {
  const { t } = useTranslation();

  return (
    <IsTenantCoordinatorOrAdmin>
      <EnhancedCard
        title={t('global.prices')}
        subtitle="Visualizzazione delle tariffe contrattuali attive">
        {!props.contractLines ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('global.no_data')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ width: '100%' }}>
            {props.contractLines.map((line, i) => (
              <ListItem
                key={line.id || i}
                disableGutters
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'between',
                  gap: 2,
                  py: 1.5,
                  px: 1,
                  borderRadius: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  transition: 'background-color 0.2s ease'
                }}>
                {/* Contenitore Informazioni a Sinistra */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    flexGrow: 1
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    Contratto: {line.contract_code}
                  </Typography>

                  {line.commission_ref && (
                    <Chip
                      label={`Ref: ${line.commission_ref}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        height: 20,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}

                  {line.laboratories && line.laboratories.length > 0 && (
                    <Chip
                      label={`Lab: ${line.laboratories.join(', ')}`}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>

                {/* Prezzo a Destra */}
                <Box
                  sx={{
                    alignSelf: { xs: 'flex-end', sm: 'center' },
                    textAlign: 'right'
                  }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                    {currencyFormatter(line.unit_price)}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </EnhancedCard>
    </IsTenantCoordinatorOrAdmin>
  );
}

export default ProductPricesCard;
